/**
 * Mahavir Jayanti cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_chaitra`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mahavirJayantiCardsPack: ToilePack = {
    slug: 'mahavir-jayanti-cards',
    category: 'faith',
    name: 'Mahavir Jayanti cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lion', label: 'Lion card', subject: 'a vertical card lettered "Happy Mahavir Jayanti" above a seated golden lion, Mahavira\'s emblem, on a lotus base, a fine border of ashtamangala signs'},
        {key: 'ahimsa', label: 'Ahimsa card', subject: 'a card with the raised open palm bearing a wheel and the word "Ahimsa" at the centre, "Live and let live" lettered beneath, a thin saffron and white border'},
        {key: 'blank', label: 'Blank card', subject: 'a card with a kalash and two lamps in the header and a frame of small lotus buds, a large clear empty panel for a personal message, "Mahavir Jayanti" lettered across the top, nothing written in the panel'},
    ],
    styles: [
        {key: 'manuscript', label: 'Manuscript', style: 'Jain manuscript painting, fine gold line, calm flat colors, red border rule', palette: 'saffron, deep red, gold and lapis on cream', framing: 'centered inside a red-ruled frame'},
        {key: 'flatsoft', label: 'Flat soft', style: 'soft flat vector illustration, rounded shapes, no outlines, gradient-free fills', palette: 'saffron, white and pale gold', framing: 'centered with a wide white margin'},
        {key: 'goldline', label: 'Gold line', style: 'fine gold line drawing, delicate ornament, restrained symmetry', palette: 'gold line on ivory', framing: 'centered with a thin gold border'},
    ],
    directions: ['Jain flag', 'temple', 'school card'],
    directionHint: 'try: Jain flag',
    seo: {
        title: 'Mahavir Jayanti cards: lion, ahimsa, blank card',
        metaDescription: 'Nine Mahavir Jayanti cards in three styles: a golden lion emblem, an ahimsa palm with its teaching and a blank card for a message.',
        h1: 'Mahavir Jayanti cards: lion, ahimsa, blank card',
        tagline: 'Nine cards in three styles: Jain manuscript painting, flat soft vector and gold line.',
        intro: "A card a Jain family or school sends for Mahavir Jayanti, the birth of Vardhamana Mahavira on the thirteenth day of bright Chaitra. These nine show a golden lion, Mahavira's emblem, on a lotus base; a raised palm with the word Ahimsa; and a blank card with a large panel for a personal message. Each is drawn in Jain manuscript painting, flat soft vector or gold line. Type a different subject and the same styles carry it.",
        useCases: [
            'a greeting to a Jain family',
            'a card for a school assembly',
            'a card with a personal message',
            'a printed card for a temple notice',
        ],
        faq: [
            {q: 'Who is Mahavira?', a: 'Mahavira, born Vardhamana, is the twenty-fourth tirthankara of Jainism, and the lion is his emblem.'},
            {q: 'What does the palm card show?', a: 'A raised open palm bearing a wheel, with the word Ahimsa at its centre, for the Jain teaching of non-violence.'},
            {q: 'Can I write a personal message?', a: 'Yes. The blank card has a large clear panel for your own words.'},
            {q: 'Is the teaching spelled as I need it?', a: 'The teaching is set as the design asks, such as Ahimsa and Live and let live. Check the lettering against your wording before printing.'},
        ],
    },
}
